import { useCallback, useMemo, useState } from 'react';

type UseBooleanSetValue = {
	off: () => void;
	on: () => void;
	toggle: () => void;
};

type UseBooleanOutput = [value: boolean, setValue: UseBooleanSetValue];

/**
 * Returns a stateful boolean value and an object containing functions to update it.
 *
 * @param defaultValue Initial value on mount.
 * @returns An array with the boolean `value` and {@link UseBooleanSetValue} functions object.
 */
export function useBoolean(defaultValue?: boolean): UseBooleanOutput {
	const [ value, setValue ] = useState(Boolean(defaultValue));

	const on = useCallback(() => setValue(true), []);
	const off = useCallback(() => setValue(false), []);
	const toggle = useCallback(() => setValue((val) => !val), []);

	const useBooleanSetValue = useMemo(
		() => ({
			off,
			on,
			toggle
		}),
		[ off, on, toggle ]
	);

	return [ value, useBooleanSetValue ];
}
